import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Button, Table, Alert, ProgressBar, Badge, Card, Row, Col } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db, storage } from '../firebase';
import { collection, doc, getDoc, getDocs, query, setDoc, serverTimestamp, Timestamp, updateDoc, where } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
  LEARNER_DOCUMENT_CATEGORIES,
  CATEGORY_HELPERS,
  DOCUMENT_CATEGORY_CONFIG,
  DOCUMENT_CATEGORY_CONFIG_BY_ID,
  RECENCY_REQUIRED,
  toCategoryId
} from '../constants/documentCategories';
import {
  MAX_DOCUMENT_SIZE_BYTES,
  DOCUMENT_ACCEPTED_FILE_EXTENSIONS,
  DOCUMENT_ACCEPTED_MIME_TYPES
} from '../constants/fileUploadLimits';
import {
  DOCUMENT_CATEGORIES as LEGACY_DOCUMENT_CATEGORIES
} from '../config/documentCategories';
import { getDocumentStatus, getConditionalMandatoryStatus } from '../config/documentCategories';
import DocumentDeadlineService from '../services/documentDeadlineService';
import { createDocumentUploadSchema } from '../validation/documentUploadSchema';
import { validateFile } from '../utils/inputValidation';
import { logSecurity } from '../utils/secureLogger';

const DocumentUpload = () => {
  const { currentUser, userProfile } = useAuth();
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState(LEARNER_DOCUMENT_CATEGORIES[0]);
  const [availableCategories, setAvailableCategories] = useState(LEARNER_DOCUMENT_CATEGORIES);
  const [programRequiredCategories, setProgramRequiredCategories] = useState([]);
  const [certificationDate, setCertificationDate] = useState('');
  const [documentVariant, setDocumentVariant] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [documents, setDocuments] = useState([]);
  const [documentStatus, setDocumentStatus] = useState(null);
  const [deadlineInfo, setDeadlineInfo] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const selectedCategoryConfig = useMemo(
    () => DOCUMENT_CATEGORY_CONFIG[category] || null,
    [category]
  );
  const hasProgramSpecificRequirements = useMemo(
    () => programRequiredCategories.length > 0,
    [programRequiredCategories]
  );
  const requiredCategorySet = useMemo(
    () => new Set(programRequiredCategories),
    [programRequiredCategories]
  );
  const recencyRequired = useMemo(
    () => RECENCY_REQUIRED.includes(category),
    [category]
  );
  const requiresVariant = useMemo(
    () => (selectedCategoryConfig?.allowedFileVariants || []).length > 0,
    [selectedCategoryConfig]
  );
  const variantOptions = useMemo(
    () => selectedCategoryConfig?.allowedFileVariants || [],
    [selectedCategoryConfig]
  );

  const isMandatoryForUser = useCallback(
    (config) => {
      if (!config) {
        return false;
      }
      if (hasProgramSpecificRequirements) {
        return requiredCategorySet.has(config.name);
      }
      return getConditionalMandatoryStatus(config.id || config.key || config.name, userProfile);
    },
    [hasProgramSpecificRequirements, requiredCategorySet, userProfile]
  );

  const resolveCategoryStatus = useCallback(
    (categoryName) => {
      if (!documentStatus || !documentStatus.documentStatus) {
        return null;
      }
      const config = DOCUMENT_CATEGORY_CONFIG[categoryName];
      const potentialKeys = [
        config?.key,
        config?.id?.toUpperCase(),
        config?.id,
        categoryName,
        categoryName.toUpperCase()
      ].filter(Boolean);

      for (const key of potentialKeys) {
        if (documentStatus.documentStatus[key]) {
          return documentStatus.documentStatus[key];
        }
      }
      return null;
    },
    [documentStatus]
  );

  const loadDocumentStatus = useCallback(async () => {
    try {
      const result = await DocumentDeadlineService.getUserDocumentStatus(currentUser.uid);
      if (result.success) {
        setDocumentStatus(result.data);
        setDeadlineInfo(result.data.deadlineInfo);
        
        if (Array.isArray(result.data.documents) && result.data.documents.length > 0) {
          setDocuments(result.data.documents);
        } else if (result.data.documentStatus) {
          const docs = [];
          Object.values(result.data.documentStatus).forEach((docInfo) => {
            if (docInfo.document) {
              docs.push(docInfo.document);
            }
          });
          setDocuments(docs);
        } else {
          setDocuments([]);
        }
      }
    } catch (error) {
      devError('Error loading document status:', error);
      setError('Failed to load documents');
    }
  }, [currentUser]);

  const getDocumentActivityTime = useCallback((document) => {
    const rawValue =
      document?.reviewedAt ||
      document?.uploadedAt ||
      document?.uploadDate ||
      document?.lastUpdated ||
      null;

    if (!rawValue) return 0;
    if (typeof rawValue?.toDate === 'function') {
      return rawValue.toDate().getTime();
    }
    const parsed = new Date(rawValue);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }, []);

  const getVisibleDocuments = useMemo(() => {
    const documentMap = new Map();

    documents
      .filter((document) => document?.deleted !== true)
      .forEach((document) => {
        const documentCategoryId =
          document?.categoryId ||
          (document?.category ? toCategoryId(document.category) : null) ||
          document?.categoryName ||
          document?.id;

        if (!documentCategoryId) {
          return;
        }

        const existing = documentMap.get(documentCategoryId);
        if (!existing || getDocumentActivityTime(document) >= getDocumentActivityTime(existing)) {
          documentMap.set(documentCategoryId, document);
        }
      });

    return Array.from(documentMap.values()).sort((a, b) => (
      getDocumentActivityTime(b) - getDocumentActivityTime(a)
    ));
  }, [documents, getDocumentActivityTime]);

  useEffect(() => {
    if (currentUser) {
      loadDocumentStatus();
    }
  }, [currentUser, loadDocumentStatus]);

  useEffect(() => {
    let isMounted = true;

    const resolveRequiredCategoryName = (rawValue) => {
      if (!rawValue || typeof rawValue !== 'string') {
        return null;
      }

      if (DOCUMENT_CATEGORY_CONFIG[rawValue]) {
        return rawValue;
      }

      if (DOCUMENT_CATEGORY_CONFIG_BY_ID[rawValue]) {
        return DOCUMENT_CATEGORY_CONFIG_BY_ID[rawValue].name;
      }

      if (LEGACY_DOCUMENT_CATEGORIES[rawValue]?.name) {
        return LEGACY_DOCUMENT_CATEGORIES[rawValue].name;
      }

      const normalized = String(rawValue).toUpperCase();
      if (LEGACY_DOCUMENT_CATEGORIES[normalized]?.name) {
        return LEGACY_DOCUMENT_CATEGORIES[normalized].name;
      }

      return null;
    };

    const loadAvailableCategories = async () => {
      if (!userProfile?.programId) {
        if (!isMounted) return;
        setProgramRequiredCategories([]);
        setAvailableCategories(LEARNER_DOCUMENT_CATEGORIES);
        return;
      }

      try {
        const programSnap = await getDoc(doc(db, 'programs', userProfile.programId));
        if (!programSnap.exists()) {
          if (!isMounted) return;
          setProgramRequiredCategories([]);
          setAvailableCategories(LEARNER_DOCUMENT_CATEGORIES);
          return;
        }

        const programData = programSnap.data() || {};
        const rawRequired = Array.isArray(programData.requiredDocuments)
          ? programData.requiredDocuments
          : [];
        const resolvedRequired = Array.from(
          new Set(rawRequired.map(resolveRequiredCategoryName).filter(Boolean))
        );

        if (!isMounted) return;

        if (resolvedRequired.length > 0) {
          setProgramRequiredCategories(resolvedRequired);
          setAvailableCategories(resolvedRequired);
        } else {
          setProgramRequiredCategories([]);
          setAvailableCategories(LEARNER_DOCUMENT_CATEGORIES);
        }
      } catch (loadError) {
        devError('Error loading program document requirements:', loadError);
        if (!isMounted) return;
        setProgramRequiredCategories([]);
        setAvailableCategories(LEARNER_DOCUMENT_CATEGORIES);
      }
    };

    loadAvailableCategories();

    return () => {
      isMounted = false;
    };
  }, [userProfile?.programId]);

  useEffect(() => {
    if (!availableCategories.length) {
      return;
    }
    if (!availableCategories.includes(category)) {
      setCategory(availableCategories[0]);
    }
  }, [availableCategories, category]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // SECURITY FIX: Enhanced file validation with security checks
      const securityValidation = validateFile(selectedFile, {
        maxSize: MAX_DOCUMENT_SIZE_BYTES,
        allowedTypes: DOCUMENT_ACCEPTED_FILE_EXTENSIONS.map((ext) => ext.replace('.', '')),
        allowedMimeTypes: Array.from(DOCUMENT_ACCEPTED_MIME_TYPES)
      });

      if (!securityValidation.isValid) {
        setError(securityValidation.errors.join('. '));
        setFile(null);
        setFieldErrors((prev) => ({
          ...prev,
          file: securityValidation.errors.join('. ')
        }));
        
        // Log security event for suspicious file uploads
        logSecurity('SUSPICIOUS_FILE_UPLOAD_ATTEMPT', {
          userId: currentUser?.uid,
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
          mimeType: selectedFile.type,
          errors: securityValidation.errors,
          severity: 'medium'
        });
        return;
      }

      // Additional category-specific validation
      const categoryValidation = validateFileForCategory(selectedFile);
      if (!categoryValidation.valid) {
        setError(categoryValidation.error);
        setFile(null);
        setFieldErrors((prev) => ({
          ...prev,
          file: categoryValidation.error
        }));
        return;
      }

      setFile(selectedFile);
      setError('');
      setFieldErrors((prev) => ({ ...prev, file: undefined }));
    }
  };

  const validateFileForCategory = (uploadedFile) => {
    if (!uploadedFile) {
      return {
        valid: false,
        error: 'Please select a file to upload.'
      };
    }

    if (
      uploadedFile.name.includes('../') ||
      uploadedFile.name.includes('..\\') ||
      uploadedFile.name.match(/[<>:"/\\|?*]/)
    ) {
      logSecurity('MALICIOUS_FILENAME_DETECTED', {
        userId: currentUser?.uid,
        fileName: uploadedFile.name,
        severity: 'high'
      });
      return {
        valid: false,
        error: 'Invalid filename. Please rename the file and try again.'
      };
    }

    const fileExtension = '.' + uploadedFile.name.split('.').pop().toLowerCase();
    if (!DOCUMENT_ACCEPTED_FILE_EXTENSIONS.includes(fileExtension)) {
      return {
        valid: false,
        error: `File format ${fileExtension} is not supported. Allowed formats: ${DOCUMENT_ACCEPTED_FILE_EXTENSIONS.join(
          ', '
        )}`
      };
    }

    return { valid: true };
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    setFieldErrors({});
    setError('');
    if (!RECENCY_REQUIRED.includes(value)) {
      setCertificationDate('');
    }
    if (!(DOCUMENT_CATEGORY_CONFIG[value]?.allowedFileVariants || []).length) {
      setDocumentVariant('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    // CRITICAL: Prevent upload if ID number is not linked
    if (!userProfile?.idNumber) {
      setError('Please link your ID number before uploading documents. Go to your Profile to link your ID number.');
      return;
    }

    const schema = createDocumentUploadSchema();
    const validation = schema.safeParse({
      category,
      file,
      certifiedAt: certificationDate,
      documentVariant: documentVariant || null
    });

    if (!validation.success) {
      const issues = {};
      validation.error.issues.forEach((issue) => {
        const pathKey = issue.path[0] || 'form';
        issues[pathKey] = issue.message;
      });
      setFieldErrors(issues);
      setError('Please fix the highlighted fields before uploading.');
      return;
    }

    const parsedData = validation.data;

    setUploading(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    try {
      const categoryConfig = DOCUMENT_CATEGORY_CONFIG[category];
      const documentsCollection = collection(db, 'users', currentUser.uid, 'documents');
      const documentRef = doc(documentsCollection);
      const timestamp = Date.now();
      const safeCategoryId = categoryConfig?.id || toCategoryId(category);

      // Create user-friendly file name: NameSurname_IDNumber_DocumentType.ext
      const getUserName = () => {
        if (userProfile?.firstName && userProfile?.lastName) {
          return `${userProfile.firstName}${userProfile.lastName}`;
        } else if (userProfile?.displayName) {
          return userProfile.displayName.replace(/\s+/g, '');
        } else if (currentUser?.displayName) {
          return currentUser.displayName.replace(/\s+/g, '');
        }
        return 'User';
      };

      const getIdNumber = () => {
        return userProfile?.idNumber || 'NoID';
      };

      const sanitizeName = (name) => {
        return name.replace(/[^a-zA-Z0-9]/g, '');
      };

      const getFileExtension = (filename) => {
        const parts = filename.split('.');
        return parts.length > 1 ? parts[parts.length - 1] : 'pdf';
      };

      const userName = sanitizeName(getUserName());
      const idNumber = getIdNumber();
      const documentType = sanitizeName(category.replace(/\s+/g, ''));
      const fileExtension = getFileExtension(file.name);
      const customFileName = `${userName}_${idNumber}_${documentType}.${fileExtension}`;

      const storagePath = `users/${currentUser.uid}/documents/${documentRef.id}/${customFileName}`;
      const storageRef = ref(storage, storagePath);

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progressValue = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progressValue);
        },
        (storageError) => {
          setError('Failed to upload file. Please try again.');
          devError(storageError);
          setUploading(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            const now = Timestamp.now();
            const uploadPayload = {
              category,
              categoryId: safeCategoryId,
              uploadedAt: serverTimestamp(),
              uploadDate: serverTimestamp(),
              certifiedAt: parsedData.certifiedAt ? Timestamp.fromDate(parsedData.certifiedAt) : null,
              status: 'pending',
              notes: '',
              categoryName: category,
              fileName: file.name,
              fileURL: downloadURL,
              filePath: storagePath,
              files: [
                {
                  name: file.name,
                  type: file.type,
                  size: file.size,
                  variant: requiresVariant ? parsedData.documentVariant : null,
                  storagePath,
                  downloadURL,
                  uploadedAt: now
                }
              ],
              uploadedBy: currentUser.uid,
              metadata: {
                recencyRequired,
                allowedVariants: categoryConfig?.allowedFileVariants || null
              }
            };

            const categoryCandidates = Array.from(
              new Set([
                safeCategoryId,
                category,
                categoryConfig?.name,
                categoryConfig?.id,
                categoryConfig?.key
              ].filter(Boolean))
            );
            const existingDocsSnapshot = await getDocs(documentsCollection);

            await Promise.all(
              existingDocsSnapshot.docs
                .filter((existingDoc) => {
                  if (existingDoc.id === documentRef.id) return false;
                  const existingData = existingDoc.data() || {};
                  const existingCandidates = [
                    existingData.categoryId,
                    existingData.category,
                    existingData.categoryName,
                    existingData.documentType
                  ].filter(Boolean);
                  return existingCandidates.some((candidate) => categoryCandidates.includes(candidate));
                })
                .map((existingDoc) => updateDoc(existingDoc.ref, {
                  deleted: true,
                  supersededBy: documentRef.id,
                  supersededAt: serverTimestamp()
                }))
            );

            await setDoc(documentRef, uploadPayload);

            setUploading(false);
            setFile(null);
            setProgress(0);
            setCertificationDate('');
            setDocumentVariant('');
            setSuccess(`✅ ${category} uploaded successfully! Your document is now pending verification.`);

            // Emit event to notify other components to refresh document status
            window.dispatchEvent(new CustomEvent('documentUploaded', {
              detail: {
                category,
                userId: currentUser.uid,
                fileName: customFileName
              }
            }));

            // Reload document status after a short delay
            setTimeout(() => {
              loadDocumentStatus();
            }, 1000);

            // Keep success message visible for 5 seconds
            setTimeout(() => {
              setSuccess('');
            }, 5000);
          } catch (firestoreError) {
            devError('Error saving document metadata:', firestoreError);
            setError('File uploaded but failed to save metadata. Please contact support.');
            setUploading(false);
          }
        }
      );
    } catch (error) {
      devError('Upload error:', error);
      setError('Failed to start upload. Please try again.');
      setUploading(false);
    }
  };

  return (
    <div className="mt-4">
      {/* Document Deadline Alert */}
      {deadlineInfo && (
        <Alert variant={deadlineInfo.isOverdue ? 'danger' : deadlineInfo.isUrgent ? 'warning' : 'info'} className="mb-4">
          <Alert.Heading>
            📋 Document Submission {deadlineInfo.isOverdue ? 'Overdue' : 'Deadline'}
          </Alert.Heading>
          {deadlineInfo.isOverdue ? (
            <p className="mb-0">
              <strong>⚠️ Your documents are {Math.abs(deadlineInfo.daysUntilDeadline)} days overdue!</strong>
              <br />Deadline was: {deadlineInfo.deadlineDate.toLocaleDateString()}
            </p>
          ) : deadlineInfo.isUrgent ? (
            <p className="mb-0">
              <strong>⏰ Urgent: Only {deadlineInfo.daysUntilDeadline} days remaining!</strong>
              <br />Deadline: {deadlineInfo.deadlineDate.toLocaleDateString()}
            </p>
          ) : (
            <p className="mb-0">
              Deadline: {deadlineInfo.deadlineDate.toLocaleDateString()} 
              ({deadlineInfo.daysUntilDeadline} days remaining)
            </p>
          )}
        </Alert>
      )}

      {/* Outstanding Documents Alert */}
      {documentStatus && (documentStatus.summary.missing > 0 || documentStatus.summary.rejected > 0) && (
        <Alert variant="danger" className="mb-4">
          <div className="d-flex align-items-center">
            <span style={{ fontSize: '1.5rem' }}>🚨</span>
            <div className="ms-3">
              <strong>Your documents are outstanding</strong>
              <div className="mt-1">
                {documentStatus.summary.missing > 0 && (
                  <div>📋 {documentStatus.summary.missing} document(s) missing</div>
                )}
                {documentStatus.summary.rejected > 0 && (
                  <div>❌ {documentStatus.summary.rejected} document(s) rejected</div>
                )}
              </div>
            </div>
          </div>
        </Alert>
      )}

      {/* ID Number Not Linked Alert */}
      {!userProfile?.idNumber && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>⚠️ ID Number Required</Alert.Heading>
          <p className="mb-2">
            You must link your ID number before uploading documents. All documents are saved with your ID number for verification purposes.
          </p>
          <Button variant="warning" size="sm" onClick={() => window.location.href = '/profile'}>
            Go to Profile to Link ID Number
          </Button>
        </Alert>
      )}

      <h3>Document Management</h3>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Document Categories Overview */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">📁 Required Documents</h5>
        </Card.Header>
        <Card.Body>
            <Row>
            {availableCategories.map((categoryName) => {
              const config = DOCUMENT_CATEGORY_CONFIG[categoryName];
              const docStatus = resolveCategoryStatus(categoryName);
              const statusInfo = docStatus && docStatus.document
                ? getDocumentStatus(docStatus.document)
                : { status: 'missing', variant: 'secondary', label: 'Not Uploaded' };
              const isRequired = isMandatoryForUser(config);

              return (
                <Col md={6} lg={4} key={categoryName} className="mb-3">
                  <Card className={`h-100 border-${statusInfo.variant}`}>
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <h6 className="mb-1">
                          {categoryName}
                          {isRequired && <span className="text-danger ms-1">*</span>}
                        </h6>
                        <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>
                      </div>
                      {CATEGORY_HELPERS[categoryName] && (
                        <div className="mt-2">
                          <small className="text-info">💡 {CATEGORY_HELPERS[categoryName]}</small>
                        </div>
                      )}
                      {statusInfo.comment && (
                        <Alert variant="warning" className="mt-2 mb-0 p-2">
                          <small><strong>Rejection reason:</strong> {statusInfo.comment}</small>
                        </Alert>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>

      {/* Upload Form */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">📤 Upload Document</h5>
        </Card.Header>
        <Card.Body>
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group controlId="documentCategory">
                  <Form.Label>Document Category <span className="text-danger">*</span></Form.Label>
                  <Form.Select value={category} onChange={(e) => handleCategoryChange(e.target.value)}>
                    {availableCategories.map((categoryName) => {
                      const config = DOCUMENT_CATEGORY_CONFIG[categoryName];
                      const labelSuffix = isMandatoryForUser(config) ? '(Required)' : '(Optional)';
                      return (
                        <option key={categoryName} value={categoryName}>
                          {categoryName} {labelSuffix}
                        </option>
                      );
                    })}
                  </Form.Select>
                  {CATEGORY_HELPERS[category] && !requiresVariant && (
                    <Form.Text className="text-info d-block">
                      💡 {CATEGORY_HELPERS[category]}
                    </Form.Text>
                  )}
                  {fieldErrors.category && (
                    <Form.Text className="text-danger d-block">
                      {fieldErrors.category}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            {recencyRequired && (
              <Row className="mt-3">
                <Col md={8}>
                  <Form.Group controlId="certificationDate">
                    <Form.Label>Certification Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      value={certificationDate}
                      max={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setCertificationDate(e.target.value)}
                    />
                    <Form.Text className="text-info d-block">
                      Document must be certified and not older than 3 months.
                    </Form.Text>
                    {fieldErrors.certifiedAt && (
                      <Form.Text className="text-danger d-block">
                        {fieldErrors.certifiedAt}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            )}

            {requiresVariant && (
              <Row className="mt-3">
                <Col md={8}>
                  <Form.Group controlId="documentVariant">
                    <Form.Label>Document Type <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={documentVariant}
                      onChange={(e) => setDocumentVariant(e.target.value)}
                    >
                      <option value="">Select document type</option>
                      {variantOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-info d-block">
                      Temporary acceptance of proof of payment and application. Final certificate required when available.
                    </Form.Text>
                    {fieldErrors.documentVariant && (
                      <Form.Text className="text-danger d-block">
                        {fieldErrors.documentVariant}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mt-3">
              <Col md={8}>
                <Form.Group controlId="documentFile">
                  <Form.Label>Select File <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleFileChange}
                    accept={DOCUMENT_ACCEPTED_FILE_EXTENSIONS.join(',')}
                  />
                  <Form.Text className="text-muted d-block">
                    Max size: {Math.round(MAX_DOCUMENT_SIZE_BYTES / (1024 * 1024))}MB.
                    Allowed formats: {DOCUMENT_ACCEPTED_FILE_EXTENSIONS.join(', ')}
                  </Form.Text>
                  {fieldErrors.file && (
                    <Form.Text className="text-danger d-block">
                      {fieldErrors.file}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            {uploading && (
              <Row className="mt-3">
                <Col md={8}>
                  <ProgressBar 
                    now={progress} 
                    label={`${progress.toFixed(1)}%`} 
                    variant="info"
                    animated
                  />
                </Col>
              </Row>
            )}
            
            <Row className="mt-3">
              <Col md={8}>
                <Button 
                  variant="primary" 
                  onClick={handleUpload} 
                  disabled={uploading || !file} 
                  size="lg"
                >
                  {uploading ? '📤 Uploading...' : '📤 Upload Document'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Documents Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">📄 My Documents ({getVisibleDocuments.length})</h5>
        </Card.Header>
        <Card.Body>
          {getVisibleDocuments.length > 0 ? (
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Status</th>
                  <th>Upload Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getVisibleDocuments.map((doc) => {
                  const statusInfo = getDocumentStatus(doc);
                  const primaryFile =
                    Array.isArray(doc.files) && doc.files.length > 0 ? doc.files[0] : null;
                  const downloadURL = primaryFile?.downloadURL || doc.fileURL || null;
                  const fileName = primaryFile?.name || doc.fileName || 'Document';
                  const uploadedAtValue =
                    (doc.uploadedAt && typeof doc.uploadedAt.toDate === 'function'
                      ? doc.uploadedAt.toDate()
                      : null) ||
                    (doc.uploadDate && typeof doc.uploadDate.toDate === 'function'
                      ? doc.uploadDate.toDate()
                      : null) ||
                    (doc.uploadedAt ? new Date(doc.uploadedAt) : null) ||
                    (doc.uploadDate ? new Date(doc.uploadDate) : null);

                  return (
                    <tr key={doc.id}>
                      <td>
                        <div>
                          <strong>{doc.category || doc.categoryName}</strong>
                          <br />
                          <small className="text-muted">{fileName}</small>
                        </div>
                      </td>
                      <td>
                        <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>
                        {statusInfo.comment && (
                          <div className="mt-1">
                            <small className="text-warning">
                              <strong>Reason:</strong> {statusInfo.comment}
                            </small>
                          </div>
                        )}
                      </td>
                      <td>{uploadedAtValue ? uploadedAtValue.toLocaleDateString() : '—'}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          href={downloadURL || '#'} 
                          target="_blank"
                          rel="noopener noreferrer"
                          disabled={!downloadURL}
                        >
                          📥 Download
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">
              📋 No documents uploaded yet. Start by uploading your required documents above.
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default DocumentUpload;
